import {
  DownloadIcon,
  PencilAltIcon,
  PhotographIcon,
} from '@heroicons/react/outline';
import katex from 'katex';
import useLocale from 'next-translate/useTranslation';
import React, {
  FC,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import { CodeProps } from 'react-markdown/lib/ast-to-react';
import { ReactMarkdownProps } from 'react-markdown/lib/complex-types';
import remarkGfm from 'remark-gfm';

import 'katex/dist/katex.min.css';

import {
  linkifyTimestamps,
  secondsFromTimestampHref,
} from '@/lib/transcriptChapters';

import {
  useSandboxChatId,
  useSandboxFileUrl,
} from '@/contexts/SandboxChatContext';

import ChatTable from './ChatTable';
import { CodeBlock } from './CodeBlock';
// import EditModal from '../images/EditModal';

interface Props {
  message: string;
  onImageEdit?: (prompt: string, newImageUrl: string) => void;
  searchResults?: boolean;
  preferLinkText?: boolean;
  /**
   * Text about a recording: its times ("12:30") become buttons that call
   * this with the seconds. Pass a function that never changes (the block is
   * memoized on the message).
   */
  onTimestampClick?: (seconds: number) => void;
  /** The recording's length: later "times" stay text (clock times). */
  timestampMaxSeconds?: number | null;
}

const TimestampButton = ({
  seconds,
  onClick,
  children,
}: {
  seconds: number;
  onClick: (seconds: number) => void;
  children: ReactNode;
}) => (
  <button
    type='button'
    onClick={(event) => {
      event.preventDefault();
      onClick(seconds);
    }}
    className='mx-px inline-flex items-baseline rounded-md bg-primary-50 px-1.5 font-medium tabular-nums text-primary-700 ring-1 ring-inset ring-primary-100 transition-colors hover:bg-primary-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-500/20 dark:hover:bg-primary-500/20'
  >
    {children}
  </button>
);

interface ImageWithDownloadProps {
  src?: string;
  alt?: string;
  onImageEdit?: (prompt: string, newImageUrl: string) => void;
}

/** A link in prose: indigo, underlined, readable in dark mode too. */
const LINK_CLASS =
  'text-primary-600 underline decoration-primary-600/30 underline-offset-2 transition-colors hover:decoration-current dark:text-primary-300 dark:decoration-primary-300/40';

// Memoize static components. The gap between items comes from globals.css
// (`.md-list > li + li`), so the chat can tighten it for its replies; the
// class also carries it in inline mode, which has no `.marky-markdown`.
const CustomList = memo(({ children }: { children: ReactNode }) => (
  <ul className='md-list list-outside list-disc ps-5 marker:text-primary-600 dark:marker:text-primary-400'>
    {processMathInNode(children)}
  </ul>
));
CustomList.displayName = 'CustomList';

// Numbered lists keep their numbers (and a list that starts at 3 starts at 3).
const CustomOrderedList = memo(
  ({ children, start }: { children: ReactNode; start?: number }) => (
    <ol
      start={start}
      className='md-list list-outside list-decimal ps-5 marker:text-primary-600 dark:marker:text-primary-400'
    >
      {processMathInNode(children)}
    </ol>
  ),
);
CustomOrderedList.displayName = 'CustomOrderedList';

// A div, not a p: items of a loose list already arrive wrapped in <p>, and a
// <p> inside a <p> is invalid HTML that breaks hydration.
// The class carries GFM's `task-list-item` (a "- [x]" row), which drops the
// bullet for its checkbox (globals.css).
const CustomListItem = memo(
  ({ children, className }: { children: ReactNode; className?: string }) => (
    <li className={className}>
      <div className='my-0'>{processMathInNode(children)}</div>
    </li>
  ),
);
CustomListItem.displayName = 'CustomListItem';

// Media URL detection helpers
const AUDIO_EXTENSIONS = [
  '.mp3',
  '.wav',
  '.ogg',
  '.aac',
  '.m4a',
  '.opus',
  '.flac',
  '.webm',
];
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];

const isMediaUrl = (url: string, extensions: string[]): boolean => {
  if (!url || !url.startsWith('http')) return false;
  const cleanUrl = url.toLowerCase().split('?')[0].split('#')[0];
  return extensions.some((ext) => cleanUrl.endsWith(ext));
};

const isAudioUrl = (url: string): boolean => isMediaUrl(url, AUDIO_EXTENSIONS);
const isVideoUrl = (url: string): boolean => isMediaUrl(url, VIDEO_EXTENSIONS);

/** The title and Download link under an inline player. */
const MediaCaption = ({ src, title }: { src: string; title: string }) => {
  const { t } = useLocale('common');
  return (
    <span className='mt-1.5 flex items-center justify-between gap-3 text-[12px] leading-5 text-[#6f6f86] dark:text-slate-400'>
      <span className='min-w-0 truncate' dir='auto'>
        {title}
      </span>
      <a
        href={src}
        download
        className='inline-flex shrink-0 items-center gap-1 font-medium text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200 [@media(pointer:coarse)]:min-h-10 [@media(pointer:coarse)]:px-1'
      >
        <DownloadIcon className='h-3.5 w-3.5' aria-hidden='true' />
        {t('Download')}
      </a>
    </span>
  );
};

// Inline players for markdown links. Spans (display from classes): the link
// sits inside a <p>, where a <div> is invalid HTML.
const InlineAudioPlayer = memo<{ src: string; title?: string }>(
  ({ src, title }) => (
    <span className='my-3 block w-full max-w-lg'>
      <audio
        controls
        src={src}
        className='block w-full rounded-full'
        preload='metadata'
      />
      {title && <MediaCaption src={src} title={title} />}
    </span>
  ),
);
InlineAudioPlayer.displayName = 'InlineAudioPlayer';

const InlineVideoPlayer = memo<{ src: string; title?: string }>(
  ({ src, title }) => (
    <span className='my-3 block w-full max-w-2xl'>
      <video
        controls
        src={src}
        className='block max-h-[min(70vh,480px)] w-auto max-w-full rounded-xl bg-black ring-1 ring-[#0f0f28]/[0.08] dark:ring-white/10'
        preload='metadata'
      />
      {title && <MediaCaption src={src} title={title} />}
    </span>
  ),
);
InlineVideoPlayer.displayName = 'InlineVideoPlayer';

/** A link chip's icon when the site's own can't be found or loaded. */
const FALLBACK_LINK_ICON = '/images/globe-icon.svg';

const showFallbackLinkIcon = (
  event: React.SyntheticEvent<HTMLImageElement>,
) => {
  const img = event.currentTarget;
  if (!img.src.endsWith(FALLBACK_LINK_ICON)) img.src = FALLBACK_LINK_ICON;
};

const CustomLink = memo<
  ReactMarkdownProps & {
    href?: string;
    searchResults?: boolean;
    preferLinkText?: boolean;
  }
>(({ href, children, preferLinkText }) => {
  const getFaviconUrl = useCallback((url: string) => {
    try {
      const hostname = new URL(url || '').hostname;
      return `https://www.google.com/s2/favicons?domain=${hostname}&sz=64`;
    } catch {
      return FALLBACK_LINK_ICON;
    }
  }, []);

  const getHostname = useCallback((url: string) => {
    try {
      return new URL(url || '').hostname;
    } catch {
      return url;
    }
  }, []);

  const getFaviconUrlFromTitle = useCallback(
    (title: string, fallbackUrl: string) => {
      const normalizedTitle = title.trim();
      const titleDomain = normalizedTitle.match(
        /(?:https?:\/\/)?(?:www\.)?([a-z0-9-]+(?:\.[a-z0-9-]+)+)/i,
      )?.[1];

      if (titleDomain) {
        return `https://www.google.com/s2/favicons?domain=${titleDomain}&sz=64`;
      }

      return getFaviconUrl(fallbackUrl);
    },
    [getFaviconUrl],
  );

  const isUrl = useCallback((text: string) => {
    return text.startsWith('http');
  }, []);

  const childrenText =
    typeof children === 'string' ? children : String(children || '');
  const hostname = getHostname(href || '');
  const childIsUrl = isUrl(childrenText);

  // Truncate long URL text for display (must be before early return to follow hooks rules)
  const displayText = useMemo(() => {
    if (childIsUrl && childrenText.length > 60) {
      try {
        const url = new URL(childrenText);
        const path = url.pathname + url.search;
        const truncatedPath =
          path.length > 30 ? path.slice(0, 30) + '...' : path;
        return url.hostname + truncatedPath;
      } catch {
        return childrenText.slice(0, 60) + '...';
      }
    }
    return children;
  }, [childIsUrl, childrenText, children]);

  // A link with no real address ("[see the thread](#)", an anchor) leads
  // nowhere from a chat message: show its text only.
  if (!href || href.startsWith('#')) {
    return <span>{children}</span>;
  }
  // Only web addresses get the site-icon chip; a relative or mailto: link
  // stays a plain link.
  const isWebLink = /^https?:\/\//i.test(href);

  // Render audio URLs as inline players
  if (href && isAudioUrl(href)) {
    const title = !childIsUrl ? childrenText : undefined;
    return <InlineAudioPlayer src={href} title={title} />;
  }

  // Render video URLs as inline players
  if (href && isVideoUrl(href)) {
    const title = !childIsUrl ? childrenText : undefined;
    return <InlineVideoPlayer src={href} title={title} />;
  }

  if (preferLinkText && isWebLink && !childIsUrl && childrenText) {
    return (
      <a
        href={href}
        className='ms-1 inline-flex max-w-full items-center gap-1.5 rounded-full bg-[#f4f4fb] px-2 py-0.5 align-middle text-[12px] font-medium leading-4 text-[#56566d] ring-1 ring-[#0f0f28]/[0.07] transition hover:text-[#10101c] hover:ring-[#0f0f28]/20 dark:bg-white/[0.06] dark:text-slate-300 dark:ring-white/10 dark:hover:text-white dark:hover:ring-white/25'
        target='_blank'
        rel='noopener noreferrer'
        title={childrenText}
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- a site's favicon */}
        <img
          src={getFaviconUrlFromTitle(childrenText, href || '')}
          width={12}
          height={12}
          alt=''
          className='h-3 w-3 shrink-0 rounded-sm'
          onError={showFallbackLinkIcon}
        />
        <span className='truncate' dir='auto'>
          {children}
        </span>
      </a>
    );
  }

  // A link to another site with its own words ("[the release schedule](…)"):
  // the words stay readable in the sentence, led by the site's icon, and the
  // site's name is the link's tooltip. Short words that don't name the site
  // (a citation "1", "Source", "here") also show the site's name after them:
  // a tooltip never shows on a touch screen.
  if (
    isWebLink &&
    !childIsUrl &&
    !href?.includes(childrenText) &&
    !href?.includes('easy-peasy.ai')
  ) {
    const site = hostname.replace(/^www\./i, '');
    const words = childrenText.trim().toLowerCase();
    const showSite =
      words.length <= 12 &&
      !site
        .toLowerCase()
        .split('.')
        .slice(0, -1)
        .some((label) => label.length >= 3 && words.includes(label));
    return (
      <a
        href={href}
        className={LINK_CLASS}
        target='_blank'
        rel='noopener noreferrer'
        title={hostname}
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- a site's favicon */}
        <img
          src={getFaviconUrl(href || '')}
          width={14}
          height={14}
          alt=''
          className='me-1 inline-block h-3.5 w-3.5 rounded-[3px] align-[-2px]'
          onError={showFallbackLinkIcon}
        />
        {children}
        {showSite && (
          <span
            className='ms-1 inline-block max-w-[14rem] truncate rounded-full bg-[#f4f4fb] px-1.5 align-[-3px] text-[12px] font-medium leading-[18px] text-[#56566d] ring-1 ring-[#0f0f28]/[0.07] dark:bg-white/[0.06] dark:text-slate-300 dark:ring-white/10'
            dir='ltr'
          >
            {site}
          </span>
        )}
      </a>
    );
  }

  return (
    <a
      href={href}
      className={LINK_CLASS}
      target='_blank'
      rel='noreferrer'
      title={childIsUrl && childrenText.length > 60 ? childrenText : undefined}
      dir={childIsUrl ? 'ltr' : undefined}
    >
      {displayText}
    </a>
  );
});
CustomLink.displayName = 'CustomLink';

// Separate the image download logic
const downloadImage = async (src: string): Promise<void> => {
  try {
    const response = await fetch(src);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `image.${src.split('.').pop() || 'png'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading image:', error);
    window.open(src, '_blank');
  }
};

/** The small white pills over an image (Edit, Download). */
const IMAGE_ACTION_CLASS =
  'inline-flex h-8 items-center gap-1.5 rounded-full bg-white/95 px-2.5 text-[12px] font-medium text-[#26263a] shadow-[0_1px_2px_rgba(16,16,40,0.12)] ring-1 ring-[#0f0f28]/10 transition-colors hover:bg-white hover:text-[#10101c] focus-visible:[outline-style:solid] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 [@media(pointer:coarse)]:h-10';

const ImageWithDownload = memo<ImageWithDownloadProps>(({ src, alt }) => {
  const { t } = useLocale('common');
  // const [showEditModal, setShowEditModal] = useState(false);

  const handleDownload = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      if (src) {
        await downloadImage(src);
      }
    },
    [src],
  );

  // const handleUpdate = useCallback(
  //   (updatedImage: UpdatedImage, prompt: string) => {
  //     if (onImageEdit && updatedImage.image_url) {
  //       onImageEdit(prompt, updatedImage.image_url);
  //     }
  //     setShowEditModal(false);
  //   },
  //   [onImageEdit]
  // );

  // Spans (not divs): react-markdown renders inline images inside <p>, and a
  // block element there is invalid HTML that fires a hydration error on
  // every page embedding markdown (chat, FAQs). display comes from classes.
  // The frame shrinks to the image, which keeps its aspect ratio and is
  // capped at 480 px tall, so four posters are not a 3,000 px wall; a click
  // opens it at full size in a new tab.
  return (
    <span className='relative my-3 block w-fit max-w-full'>
      <a
        href={src}
        target='_blank'
        rel='noreferrer'
        title={t('Open full size')}
        aria-label={alt || t('Open full size')}
        className='block cursor-zoom-in rounded-xl focus-visible:[outline-style:solid] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- any URL a reply links */}
        <img
          src={src}
          alt={alt}
          className='block h-auto max-h-[480px] w-auto max-w-full rounded-xl ring-1 ring-[#0f0f28]/[0.08] dark:ring-white/10'
        />
      </a>
      {/* <EditModal
          image={{ image_url: src }}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onUpdate={handleUpdate}
        /> */}
      {src && (
        <span className='absolute end-2 top-2 flex gap-1.5'>
          <a
            href={`/ai-image-editor?imageUrl=${encodeURIComponent(src)}`}
            target='_blank'
            rel='noreferrer'
            className={IMAGE_ACTION_CLASS}
            title={t('Edit image')}
            aria-label={t('Edit image')}
          >
            <PencilAltIcon className='h-4 w-4' aria-hidden='true' />
            <span className='max-sm:hidden'>{t('Edit')}</span>
          </a>
          <a
            href={src}
            download={`image.${src.split('.').pop() || 'png'}`}
            className={IMAGE_ACTION_CLASS}
            title={t('Download image')}
            aria-label={t('Download image')}
            onClick={handleDownload}
          >
            <DownloadIcon className='h-4 w-4' aria-hidden='true' />
            <span className='max-sm:hidden'>{t('Download')}</span>
          </a>
        </span>
      )}
    </span>
  );
});
ImageWithDownload.displayName = 'ImageWithDownload';

// Models often embed SANDBOX paths in markdown (![chart](/home/user/x.png)) —
// especially less capable ones that skip uploading to public storage — and the
// exact shape varies by model: full path, ~/chart.png, ./chart.png or a bare
// chart.png (relative to the sandbox home dir, where all tools write). Resolve
// every non-URL src to a sandbox path and fetch a presigned URL for it.
const NON_SANDBOX_SRC_RE = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;

const toSandboxPath = (src: string | undefined): string | null => {
  if (!src || NON_SANDBOX_SRC_RE.test(src)) return null;
  if (src.startsWith('/')) return src;
  return `/home/user/${src.replace(/^(?:\.\/|~\/)/, '')}`;
};

const SandboxAwareImage = memo<ImageWithDownloadProps>(
  ({ src, alt, onImageEdit }) => {
    const { t } = useLocale('common');
    const chatId = useSandboxChatId();
    // Outside a chat (the employee Computer tab) a resolver takes its place.
    const resolveFileUrl = useSandboxFileUrl();
    const canResolve = Boolean(chatId || resolveFileUrl);
    const sandboxPath = toSandboxPath(src);
    const isSandboxPath = sandboxPath !== null;
    const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
      if (!sandboxPath || (!chatId && !resolveFileUrl)) return;
      let cancelled = false;
      const urlRequest: Promise<string | null> = resolveFileUrl
        ? resolveFileUrl(sandboxPath)
        : fetch('/api/sandbox/files/download', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ chatId, path: sandboxPath }),
          })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => (data?.url as string | undefined) ?? null);
      urlRequest
        .then((url) => {
          if (cancelled) return;
          if (!url) {
            setFailed(true);
            return;
          }
          // The presign endpoint doesn't check existence — probe the URL so a
          // deleted file (expired sandbox) shows the placeholder, not a broken img.
          const probe = new window.Image();
          probe.onload = () => {
            if (!cancelled) setResolvedUrl(url);
          };
          probe.onerror = () => {
            if (!cancelled) setFailed(true);
          };
          probe.src = url;
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
      return () => {
        cancelled = true;
      };
    }, [sandboxPath, chatId, resolveFileUrl]);

    if (isSandboxPath && !resolvedUrl) {
      if (failed || !canResolve) {
        return (
          <span className='my-1 inline-flex max-w-full items-center gap-1.5 rounded-lg bg-[#f4f4fb] px-2 py-1 align-middle text-[12px] leading-5 text-[#6f6f86] ring-1 ring-[#0f0f28]/[0.07] dark:bg-white/[0.05] dark:text-slate-400 dark:ring-white/10'>
            <PhotographIcon
              className='h-3.5 w-3.5 shrink-0 text-[#8b8ba0] dark:text-slate-500'
              aria-hidden='true'
            />
            <span className='shrink-0' dir='auto'>
              {alt || t('Image')}:
            </span>
            <span className='min-w-0 truncate font-mono' dir='ltr'>
              {src}
            </span>
            {failed && (
              <span className='shrink-0'>
                ({t('file no longer available')})
              </span>
            )}
          </span>
        );
      }
      return (
        <span className='my-3 block aspect-video w-full max-w-md animate-pulse rounded-xl bg-[#efeff7] dark:bg-white/[0.06]' />
      );
    }

    return (
      <ImageWithDownload
        src={resolvedUrl ?? src}
        alt={alt}
        onImageEdit={onImageEdit}
      />
    );
  },
);
SandboxAwareImage.displayName = 'SandboxAwareImage';

// Format the content for LaTeX rendering
const formatForLatex = (content: string) => {
  // If it's a LaTeX environment, don't touch it
  if (/\\begin\{.*?\}/.test(content) || /\\end\{.*?\}/.test(content)) {
    return content;
  }
  // Remove any trailing \n and replace all \n with space
  content = content.replace(/\\n/g, ' ').trim();
  // Handle division formatting: convert (2x) / 2 to \frac{2x}{2}
  content = content.replace(/\((.*?)\)\s*\/\s*(\d+)/g, '\\frac{$1}{$2}');
  // Handle basic division with / sign
  content = content.replace(/(\w+)\s*\/\s*(\w+)/g, '\\frac{$1}{$2}');
  // Handle subtraction with proper spacing
  content = content.replace(/(\w+)\s*−\s*(\w+)/g, '$1 - $2');
  // Add proper spacing around operations
  content = content.replace(/(\w+)([+\-=])(\w+)/g, '$1 $2 $3');
  return content;
};

const processMathString = (text: string) => {
  // Regex for custom LaTeX delimiter: <<| ... |>> (multi-line) and &lt;&lt;| ... |&gt;&gt; (escaped)
  const regex = /<<\|(.*?)\|>>|&lt;&lt;\|(.*?)\|&gt;&gt;/gs;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    const [fullMatch, unescaped, escaped] = match;
    const content = unescaped ?? escaped;
    const offset = match.index;
    // Push preceding text
    if (lastIndex < offset) {
      parts.push(text.slice(lastIndex, offset));
    }
    // Render as math using KaTeX directly (avoids react-latex-next
    // delimiter parsing which conflicts with \$ in content)
    const formattedContent = formatForLatex(content);
    try {
      const html = katex.renderToString(formattedContent.trim(), {
        displayMode: /\n/.test(content),
        throwOnError: false,
      });
      parts.push(
        <span
          key={offset + '-math'}
          dangerouslySetInnerHTML={{ __html: html }}
        />,
      );
    } catch {
      parts.push(formattedContent);
    }
    lastIndex = offset + fullMatch.length;
  }
  // Push any remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length === 1 ? parts[0] : parts;
};

// Helper to process any ReactNode (string, <br>, array, etc)
const processMathInNode = (node: ReactNode): ReactNode => {
  if (typeof node === 'string') {
    return processMathString(node);
  }
  if (Array.isArray(node)) {
    return node.map((child, i) => (
      <React.Fragment key={i}>{processMathInNode(child)}</React.Fragment>
    ));
  }
  // Recurse into inline formatting elements (bold, italic, etc.)
  // so <<|...|>> inside **bold** or *italic* is still processed
  if (React.isValidElement(node)) {
    const type = node.type;
    if (
      type === 'strong' ||
      type === 'em' ||
      type === 'b' ||
      type === 'i' ||
      type === 'del'
    ) {
      return React.cloneElement(
        node,
        {},
        processMathInNode((node.props as any).children),
      );
    }
  }
  return node;
};

// Pre-process <<|...|>> math delimiters before markdown parsing:
// 1. Escape | in delimiters so GFM tables don't split them as columns
// 2. Double backslashes inside so markdown doesn't consume LaTeX escapes
//    (e.g. \$ → \\$ → markdown unescapes to \$ → KaTeX renders as $)
// `text` is typed as string but reaches us from callers that pass optional
// fields (e.g. an absent `request_body.keywords`), so guard rather than throw.
const escapeMathDelimiters = (text: string): string =>
  (text ?? '').replace(/<<\|([\s\S]*?)\|>>/g, (_match, content: string) => {
    const escaped = content.replace(/\\/g, '\\\\');
    return '<<\\|' + escaped + '\\|>>';
  });

const MarkdownBlock: FC<Props & { inline?: boolean }> = memo(
  ({
    message,
    onImageEdit,
    searchResults,
    preferLinkText = false,
    inline = false,
    onTimestampClick,
    timestampMaxSeconds,
  }) => {
    const components = useMemo(
      () => ({
        // A fence with a language becomes the dark CodeBlock. One without
        // (plain text, poems, ASCII art) stays a wrapped <pre><code>, as on
        // every Markdown surface before; inline code stays a <code>.
        code({
          node: _node,
          inline,
          className,
          children,
          ...props
        }: CodeProps) {
          const match = /language-([\w+#.-]+)/.exec(className || '');
          return !inline && match ? (
            <CodeBlock
              language={match[1]}
              value={String(children).replace(/\n$/, '')}
              {...props}
            />
          ) : (
            <code className={className} dir='ltr' {...props}>
              {children}
            </code>
          );
        },
        // CodeBlock draws its own panel and <pre>: react-markdown's <pre>
        // around it would be a <pre><div><pre>. A fence without a language
        // keeps the plain <pre> (`.md-pre` in globals.css).
        pre: ({ children }: { children: ReactNode }) => {
          const child = React.Children.toArray(children)[0];
          const className = React.isValidElement(child)
            ? (child.props as { className?: unknown }).className
            : undefined;
          return typeof className === 'string' &&
            /language-[\w+#.-]+/.test(className) ? (
            <>{children}</>
          ) : (
            <pre className='md-pre'>{children}</pre>
          );
        },
        img: ({ src, alt }: { src?: string; alt?: string }) => (
          <SandboxAwareImage src={src} alt={alt} onImageEdit={onImageEdit} />
        ),
        ul: CustomList,
        ol: CustomOrderedList,
        li: CustomListItem,
        a: (props: ReactMarkdownProps & { href?: string }) => {
          const seconds = onTimestampClick
            ? secondsFromTimestampHref(props.href)
            : null;
          if (seconds !== null && onTimestampClick) {
            return (
              <TimestampButton seconds={seconds} onClick={onTimestampClick}>
                {props.children}
              </TimestampButton>
            );
          }
          return (
            <CustomLink
              {...props}
              searchResults={searchResults}
              preferLinkText={preferLinkText}
            />
          );
        },
        p: ({ children }: { children: ReactNode }) => (
          <p>{processMathInNode(children)}</p>
        ),
        // Tables scroll sideways in their own container (wide tables stay
        // readable on phones) and get Copy, Download and full-screen actions.
        table: ({ children }: { children: ReactNode }) => (
          <ChatTable>{children}</ChatTable>
        ),
        // Keep the column alignment (`---:`) react-markdown passes as style.
        td: ({
          children,
          style,
        }: {
          children: ReactNode;
          style?: React.CSSProperties;
        }) => <td style={style}>{processMathInNode(children)}</td>,
        th: ({
          children,
          style,
        }: {
          children: ReactNode;
          style?: React.CSSProperties;
        }) => <th style={style}>{processMathInNode(children)}</th>,
        span: ({ children }: { children: ReactNode }) => (
          <span>{processMathInNode(children)}</span>
        ),
      }),
      [onImageEdit, searchResults, preferLinkText, onTimestampClick],
    );

    const markdown = useMemo(
      () =>
        onTimestampClick
          ? linkifyTimestamps(message, { maxSeconds: timestampMaxSeconds })
          : message,
      [message, onTimestampClick, timestampMaxSeconds],
    );

    // Pre-process the message if inline mode is true
    const processedMessage = useMemo(() => {
      if (inline && typeof message === 'string' && /\[.*?\]/g.test(message)) {
        const processed = message.replace(/\[(.*?)\]/g, (match, content) => {
          if (/^(Problem|Analysis|Solution|Answer)$/i.test(content.trim())) {
            return match;
          }
          const formattedContent = formatForLatex(content);
          const isEquation = content.includes('=');
          if (isEquation) {
            return `$$${formattedContent}$$`;
          } else {
            return `$${formattedContent}$`;
          }
        });
        return processed;
      }
      return message;
    }, [message, inline]);

    return inline ? (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components as any}
        unwrapDisallowed
        disallowedElements={['p', 'div']}
      >
        {escapeMathDelimiters(
          typeof processedMessage === 'string' ? processedMessage : message,
        )}
      </ReactMarkdown>
    ) : (
      <div className='marky-markdown' dir='auto'>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={components as any}
        >
          {escapeMathDelimiters(markdown)}
        </ReactMarkdown>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.message === nextProps.message &&
      prevProps.preferLinkText === nextProps.preferLinkText &&
      prevProps.onTimestampClick === nextProps.onTimestampClick &&
      prevProps.timestampMaxSeconds === nextProps.timestampMaxSeconds
    );
  },
);

MarkdownBlock.displayName = 'MarkdownBlock';

export default MarkdownBlock;
